import React, { useState, useEffect, useRef, useCallback } from 'react';
import './oncorisk-table.css';
import { getApiEndpoint, getUiConfig, getItemById, getItemColor } from '../../config/oncorisk-config';

const tableColumnConfigs = {
  assertions: [
    'name', 'molecular_profile_id', 'disease_id', 'therapy_ids', 'summary',
    'assertion_type', 'assertion_direction', 'significance', 'amp_level', 'evidence_ids'
  ],
  evidence: [
    'name', 'molecular_profile_id', 'disease_id', 'therapy_ids', 'description',
    'evidence_type', 'evidence_direction', 'significance', 'variant_origin', 'rating'
  ],
  molecularprofiles: [
    'name', 'aliases', 'variants',
    '_assertions', 'associated_diseases', 'associated_evidence','molecular_profile_score',
  ],
  features: [
    'name', 'type', 'aliases',                 // originals
    'variants',                                // resolved list of variants
    'associated_diseases', 'associated_therapies',
    'associated_evidence',  'associated_assertions'
     ],
     variants: [
      'name',
      'feature_id',
      'variant_aliases', 
      'associated_diseases', 'associated_therapies',
      '_molecular_profiles', '_variant_groups'
    ],
  variantgroups: [
    'name', 'variant_ids', 'feature_id', 'evidence_ids'
  ],
  diseases: [
    'name', 'doid', 'aliases',
    '_evidence_items', '_assertions',
    'associated_features'          // ← NEW
  ],
  therapies: [
    'name', 'ncit_id', 'aliases', '_evidence_items', '_assertions'
  ],
  phenotypes: [
    'name', 'hpo_id', '_evidence_items', '_assertions'
  ],
  sources: [
    'citation', 'title', 'author_string', 'source_type', 'citation_id',
    'publication_date', 'journal', '_evidence_items'
  ]
};

const handleCellClick = (e, itemType, item, onNavigate) => {
  e.stopPropagation();
  if (!onNavigate) return;
  
  const itemId = (typeof item === 'object' && item !== null) ? item.id : item;
  const itemName = (typeof item === 'object' && item !== null) ? item.name : '';
  
  let correctedType = itemType;
  // keep “evidence” singular, pluralize everything else
  if (itemType === 'evidence') {
    correctedType = 'evidence';
  } else if (itemType && itemType.endsWith('s')) {
    // already plural
  } else if (itemType === 'therapy') {
    correctedType = 'therapies';
  } else if (itemType && !itemType.endsWith('s')) {
    correctedType = itemType + 's';
  }

  onNavigate(correctedType, itemId, itemName);
};
const ChipItem = ({ item, info, color, onNavigate }) => {
  const itemName = (typeof item === 'object' && item !== null) ? (item.name || item.id || String(item)) : String(item);
  const displayItemName = itemName.length > 15 ? itemName.substring(0, 13) + '...' : itemName;

  if (!info || !info.type || !onNavigate) { // No icon or navigation if info or onNavigate is missing
    return <span className="OCRP-chip" title={itemName} style={color ? { borderColor: color } : {}}>{displayItemName}</span>;
  }
  
  // Properly format the navigation type using the same logic as nameCell
  let navigationTypeForChip = info.table || info.type;
  
  // Format according to the same rules used in handleCellClick and nameCell
  if (navigationTypeForChip === 'evidence') {
    // Keep "evidence" singular
    navigationTypeForChip = 'evidence';
  } else if (navigationTypeForChip === 'therapy') {
    // Special case for therapy → therapies
    navigationTypeForChip = 'therapies';
  } else if (navigationTypeForChip && !navigationTypeForChip.endsWith('s')) {
    // Pluralize everything else that doesn't already end with 's'
    navigationTypeForChip = navigationTypeForChip + 's';
  }

  return (
    <span
      className="OCRP-chip OCRP-interactive-chip"
      onClick={e => {
        e.stopPropagation();
        const itemId   = (typeof item === 'object' && item !== null) ? item.id   : item;
        const itemName = (typeof item === 'object' && item !== null) ? item.name : '';
        onNavigate(navigationTypeForChip, itemId, itemName);
      }}
      title={`View details for ${itemName}`}
      style={{
        borderColor:    color,
        backgroundColor: color ? color.replace(/0\.\d+(?=\s*\))/, '0.2') : undefined
      }}
    >
      {info.icon && <i className={`fas fa-${info.icon}`}></i>}
      {displayItemName}
    </span>
  );
};

const HoverPopup = ({ items, isInfoPopup = false }) => {
  if (isInfoPopup && typeof items === 'string') {
    return (
      <div className="OCRP-id-popup">
        <div className="OCRP-popup-item">{items}</div>
      </div>
    );
  }
  
  const displayItems = Array.isArray(items) ? items : (items != null ? [items] : []);

  return (
    <div className="OCRP-id-popup">
      {displayItems.map((item, idx) => (
        <div key={idx} className="OCRP-popup-item">
          {(typeof item === 'object' && item !== null) ? (item.name || item.id || JSON.stringify(item)) : String(item)}
        </div>
      ))}
    </div>
  );
};


const CellFormatters = {
  nameCell: (value, item, tableType, onNavigate) => {
    if (!value) return '';
    let itemTypeForConfig = tableType;
    if (tableType === 'evidence') {
      itemTypeForConfig = 'evidence';
    } else if (tableType === 'therapy') {
      itemTypeForConfig = 'therapies';
    } else if (!tableType.endsWith('s')) {
      itemTypeForConfig = tableType + 's';
    }
    const config = getItemById(itemTypeForConfig);
    const singularTableType = tableType.endsWith('ies') ? tableType.slice(0, -3) + 'y' :
                              tableType.endsWith('s')   ? tableType.slice(0, -1)        :
                              tableType;
    const color = getItemColor(singularTableType);    
    return (
      <div
        className="OCRP-chip OCRP-interactive-chip"
        onClick={(e) => handleCellClick(e, itemTypeForConfig, item, onNavigate)}
        title={`View details for ${value}`}
        style={{ borderColor: color, backgroundColor: color.replace(/0\.\d+(?=\s*\))/, '0.2') }}
      >
        <i className={`fas fa-${config?.icon || 'info-circle'}`}></i>
        <span>{value.length > 20 ? value.substring(0, 18) + '...' : value}</span>
      </div>
    );
  },

  objectRef: (value, column, onNavigate) => {
    if (!value?.name) return '';
    const typeMap = {
      disease_id:            { type: 'disease',           table: 'diseases',          icon: 'heartbeat' },
      molecular_profile_id:  { type: 'molecularprofile',  table: 'molecularprofiles', icon: 'dna'       },
      feature_id:            { type: 'feature',           table: 'features',          icon: 'dna'       },
    };
    const info = typeMap[column];
    if (!info) return value.name;
    const color = getItemColor(info.type);
    return (
      <div
        className="OCRP-chip OCRP-interactive-chip"
        onClick={(e) => handleCellClick(e, info.table, value, onNavigate)}
        title={`View details for ${value.name}`}
        style={{ borderColor: color, backgroundColor: color.replace(/0\.\d+(?=\s*\))/, '0.2') }}
      >
        <i className={`fas fa-${info.icon}`}></i>
        <span>{value.name.length > 20 ? value.name.substring(0, 18) + '...' : value.name}</span>
      </div>
    );
  },

  arrayItems: (value, column, onNavigate, itemTableTypeContext) => {
    if (!Array.isArray(value) || value.length === 0) return '';
  
    // ──────── alias columns: always plain white, not clickable ────────
    if (column.toLowerCase().includes('alias')) {
      const aliases = value;
      const white = '#fff';
  
      const renderAliasChips = (items) => (
        <div className="OCRP-chip-container">
          {items.map((alias, idx) => {
            const name = typeof alias === 'object'
              ? alias.name || alias.id || String(alias)
              : String(alias);
            return (
              <span
                key={idx}
                className="OCRP-chip"
                style={{
                  color: white,
                  borderColor: white,
                  backgroundColor: 'transparent',
                  cursor: 'default',
                  opacity: 0.75
                }}
                title={name}
              >
                {name.length > 15 ? name.slice(0, 13) + '…' : name}
              </span>
            );
          })}
        </div>
      );
  
      if (aliases.length > 2) {
        return (
          <div className="OCRP-id-collection">
            {renderAliasChips(aliases.slice(0, 2))}
            <span
              className="OCRP-chip OCRP-more-chip"
              style={{
                color: white,
                borderColor: white,
                backgroundColor: 'transparent',
                cursor: 'default',
                opacity: 0.75
              }}
            >
              +{aliases.length - 2}
            </span>
            <HoverPopup items={aliases} />
          </div>
        );
      }
  
      return renderAliasChips(aliases);
    }
  
    // ───── allow `_evidence_items` through to chip logic ─────
    if (column !== '_evidence_items' && column.startsWith('_') && typeof value === 'string') {
      // all other underscored string fields stay counts
      return CellFormatters.countField(value);
    }
  
    // ───── all other multi‐item columns (including evidence_items, _evidence_items) ─────
    const generalTypeMap = {
      therapy_ids:           { type: 'therapy',         table: 'therapies',        icon: 'pills'       },
      evidence_ids:          { type: 'evidence',        table: 'evidence',         icon: 'file-alt'    },
      _assertions:           { type: 'assertion',       table: 'assertions',       icon: 'check-square'},
      _variants:             { type: 'variant',         table: 'variants',         icon: 'dna'         },
      _evidence_items:       { type: 'evidence',        table: 'evidence',         icon: 'file-alt'    },
      variants:              { type: 'variant',         table: 'variants',         icon: 'dna'         },
      variant_ids:           { type: 'variant',         table: 'variants',         icon: 'dna'         },
      variant_aliases:       { type: 'variant',         table: 'variants',         icon: 'tags'        },
      variant_types:         { type: 'variant',         table: 'variants',         icon: 'tag'         },
      associated_diseases:   { type: 'disease',         table: 'diseases',         icon: 'heartbeat'   },
      associated_therapies:  { type: 'therapy',         table: 'therapies',        icon: 'pills'       },
      associated_assertions: { type: 'assertion',       table: 'assertions',       icon: 'check-square'},
      associated_evidence:   { type: 'evidence',        table: 'evidence',         icon: 'file-alt'    },
      associated_features:   { type: 'feature',         table: 'features',         icon: 'dna'         },
      _molecular_profiles:   { type: 'molecularprofile',table: 'molecularprofiles',icon: 'dna'         },
      _variant_groups:       { type: 'variantgroup',    table: 'variantgroups',    icon: 'layer-group' }
    };
  
    let info = generalTypeMap[column] || null;
  
    // allow manual override for "aliases" key if ever needed:
    if (!info && column === 'aliases') {
      const singular = itemTableTypeContext.endsWith('ies')
        ? itemTableTypeContext.slice(0,-3)+'y'
        : itemTableTypeContext.endsWith('s')
          ? itemTableTypeContext.slice(0,-1)
          : itemTableTypeContext;
      info = { type: singular, table: `${itemTableTypeContext}${itemTableTypeContext.endsWith('s')?'':'s'}`, icon:'tags' };
    }
    if (!info) return '';
  
    const color = getItemColor(info.type);
    const itemsToRender = value;
  
    // plus‐N logic
    if (itemsToRender.length > 2) {
      return (
        <div className="OCRP-id-collection">
          <div className="OCRP-chip-container">
            {itemsToRender.slice(0,2).map((sub, i) =>
              <ChipItem
                key={i}
                item={sub}
                info={info}
                color={color}
                onNavigate={onNavigate}
              />
            )}
            <span className="OCRP-chip OCRP-more-chip" style={{ borderColor: color, backgroundColor: color.replace(/,\s*\d+\.\d+\)/, ',0.1)') }}>
              +{itemsToRender.length - 2}
            </span>
          </div>
          <HoverPopup items={itemsToRender} />
        </div>
      );
    }
  
    return (
      <div className="OCRP-chip-container">
        {itemsToRender.map((sub, i) => (
          <ChipItem
            key={i}
            item={sub}
            info={info}
            color={color}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  },
  
  

  rating: (value) => {
    if (isNaN(Number(value))) return value;
    const rating = Number(value);
    return (
      <div className="OCRP-star-rating">
        {[1, 2, 3, 4, 5].map(i => (<i key={i} className={`fas fa-star ${i <= rating ? 'OCRP-star-filled' : 'OCRP-star-empty'}`}></i>))}
      </div>
    );
  },

  iconWithText: (value, column) => {
    const iconMap = {
      evidence_type: 'file-alt', evidence_direction: 'compass', significance: 'exclamation-circle',
      variant_origin: 'dna', assertion_type: 'check-circle', assertion_direction: 'arrow-right',
      amp_level: 'level-up-alt'
    };
    return (
      <div className="OCRP-id-collection">
        <div className="OCRP-icon-value"><i className={`fas fa-${iconMap[column] || 'info-circle'}`}></i></div>
        <HoverPopup items={value ?? 'Not specified'} isInfoPopup={true} />
      </div>
    );
  },

  countField: (value) => { // Expects string input
    if (typeof value !== 'string') return '0';
    try {
      if (value.includes('CIViC')) return (value.match(/<CIViC/g) || []).length.toString();
      if (value.startsWith('[') && value.endsWith(']')) {
        if (value === '[]') return '0';
        // This is a risky parse if not actual JSON.
        // Consider simple comma count if JSON.parse fails often.
        try {
          return JSON.parse(value.replace(/'/g, '"')).length.toString();
        } catch {
          return (value.match(/,/g) || []).length + 1
        }
      }
      return '0';
    } catch { return '0'; }
  }
};


const formatCellValue = (column, value, item, tableType, onNavigate) => {
  if (value === null || value === undefined) return '';

  // Priority 1: Explicit formatters
  if (column === 'name') return CellFormatters.nameCell(value, item, tableType, onNavigate);
  if (['molecular_profile_id', 'disease_id', 'feature_id'].includes(column)) return CellFormatters.objectRef(value, column, onNavigate);
  if (column === 'rating') return CellFormatters.rating(value);
  if (['evidence_type', 'evidence_direction', 'significance', 'variant_origin', 'assertion_type', 'assertion_direction', 'amp_level'].includes(column)) {
    return CellFormatters.iconWithText(value, column);
  }
  const arrayChipColumns = [
    'aliases', 'variants',
    'variant_aliases', 'variant_types',
    '_assertions', '_molecular_profiles', '_variant_groups',
    'associated_diseases', 'associated_therapies',
    'associated_evidence', 'associated_assertions',
    'associated_features',
    'evidence_items',   
    '_evidence_items',
    'evidence_ids',
    'therapy_ids'    // ← add this so therapies render as chips
 ];
  
  // Priority 2: Array-like columns that should be chips
  if (arrayChipColumns.includes(column)) {
    // Ensure value is an array. If it's a string representation, try to parse it.
    let arrayValue = value;
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        arrayValue = JSON.parse(value.replace(/'/g, '"'));
      } catch (e) {
        console.warn(`Could not parse string to array for column ${column}:`, value, e);
        // Fallback to string display if parsing fails
      }
    } else if (column === 'aliases' && typeof value === 'string' && !value.startsWith('[')) {
        // Handle comma-separated aliases string not in bracket format
        arrayValue = value.split(',').map(s => s.trim()).filter(Boolean);
    }


    if (Array.isArray(arrayValue)) {
      return CellFormatters.arrayItems(arrayValue, column, onNavigate, tableType);
    }
    // If not an array after attempting parse, fall through to string/object display
  }
  
  // Priority 3: '_' prefixed columns that are strings should be counts
  if (column.startsWith('_') && typeof value === 'string') {
    return CellFormatters.countField(value);
  }

  // Fallbacks
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(v => (typeof v === 'object' ? v.name || v.id : v)).join(', '); // Simple comma list for other arrays
  if (typeof value === 'object') return JSON.stringify(value);
  
  return String(value);
};


function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function(...args) {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

function OncoRiskTable({
    id,
    showActions = true,
    customTitle = null,
    onNavigate,
    filters = {},           // ← NEW
    data: initialData
  }) {
  const [tableData, setTableData] = useState(initialData
    ? { data: initialData, page: 1, pageSize: initialData.length, totalPages: 1, totalRows: initialData.length }
    : null
  );
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const uiConfig = getUiConfig();
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: uiConfig.pagination.defaultPageSize,
    totalPages: 0,
    totalRows: 0
  });
  
  const tableWrapperRef = useRef(null);

  // Console log first few rows of data when tableData updates
  useEffect(() => {
    if (tableData && tableData.data && tableData.data.length > 0) {
      console.log(`OncoRiskTable [${id}] - First 30 rows of data:`, tableData.data.slice(0, 3));
      // To inspect a specific problematic column for all first 3 items:
      // tableData.data.slice(0, 3).forEach((item, index) => {
      //   console.log(`Row ${index}, _assertions:`, item._assertions);
      //   console.log(`Row ${index}, _variants:`, item._variants);
      // });
    }
  }, [tableData, id]);


  const fetchTableData = useCallback(async (page = 1, pageSize = uiConfig.pagination.defaultPageSize) => {
    try {
      const payload = { tableName: id, page, pageSize };
      if (Object.keys(filters).length) payload.filters = filters;

      const response = await fetch(getApiEndpoint('browseTable'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      // No client-side parsing here; formatCellValue will handle string arrays if necessary
      return await response.json();
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [id]);

  const handleLoadMore = useCallback(async () => {
    if (loading || pagination.page >= pagination.totalPages) {
      return;
    }

    setLoading(true);
    setError(null);
    const nextPage = pagination.page + 1;
    const pageSizeToFetch = pagination.pageSize;

    const apiResponse = await fetchTableData(nextPage, pageSizeToFetch);

    if (apiResponse) {
      if (apiResponse.data && apiResponse.data.length > 0) {
        setTableData(prevTableData => ({
          ...apiResponse,
          data: [...(prevTableData?.data || []), ...apiResponse.data]
        }));
        setPagination({
          page: apiResponse.page,
          pageSize: apiResponse.pageSize,
          totalPages: apiResponse.totalPages,
          totalRows: apiResponse.totalRows
        });
      } else {
        setPagination(prev => ({
          ...prev,
          page: Math.min(prev.page, apiResponse.totalPages || prev.totalPages),
          totalPages: apiResponse.totalPages,
          totalRows: apiResponse.totalRows
        }));
        setTableData(prev => {
          if (!prev) return apiResponse;
          return {
            ...prev,
            totalPages: apiResponse.totalPages,
            totalRows: apiResponse.totalRows,
            page: apiResponse.page 
          };
        });
      }
    }
    setLoading(false);
  }, [loading, pagination, fetchTableData]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      const initialApiResponse = await fetchTableData(1, uiConfig.pagination.defaultPageSize);
      if (initialApiResponse) {
        setTableData(initialApiResponse);
        setPagination({
          page: initialApiResponse.page,
          pageSize: initialApiResponse.pageSize,
          totalPages: initialApiResponse.totalPages,
          totalRows: initialApiResponse.totalRows
        });
      }
      setLoading(false);
    };
    loadInitialData();
  }, [id, fetchTableData, uiConfig.pagination.defaultPageSize]);

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    if (!tableWrapper) return;

    const handleScroll = () => {
      if (loading || pagination.page >= pagination.totalPages) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = tableWrapper;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        handleLoadMore();
      }
    };
    
    const throttledScrollHandler = throttle(handleScroll, 250);
    tableWrapper.addEventListener('scroll', throttledScrollHandler);
    return () => tableWrapper.removeEventListener('scroll', throttledScrollHandler);
  }, [loading, pagination, handleLoadMore]);

  const handlePageNavigation = async (newPage) => {
    if (loading || newPage < 1 || newPage > (pagination.totalPages || 1) || newPage === pagination.page) return;
    setLoading(true);
    setError(null);
    const apiResponse = await fetchTableData(newPage, pagination.pageSize);
    if (apiResponse) {
      setTableData(apiResponse); 
      setPagination({
        page: apiResponse.page,
        pageSize: apiResponse.pageSize,
        totalPages: apiResponse.totalPages,
        totalRows: apiResponse.totalRows
      });
    }
    setLoading(false);
  };

  const getColumns = () => {
    if (tableColumnConfigs[id]) {
      return tableColumnConfigs[id];
    }
    if (tableData && tableData.data && tableData.data.length > 0) {
      return Object.keys(tableData.data[0]);
    }
    return [];
  };

  const getColumnWidth = (column) => {
    if (['evidence_type', 'evidence_direction', 'significance', 'variant_origin',
         'assertion_type', 'assertion_direction', 'amp_level'].includes(column)) return '6vh';
    if (column === 'summary' || column === 'description') return '30vh';
    if (column === 'therapy_ids' || column === 'evidence_ids' || column === '_assertions' || column === '_variants' || column === '_evidence_items' || column === 'aliases' || column === 'variants' || column === 'variant_ids') return '12vh'; // Adjusted for potential chip arrays
    if (column === 'name' || column === 'title') return '8vh';
    if (column === 'disease_id' || column === 'molecular_profile_id') return '9vh';
    if (column === 'molecular_profile_score') return '5vh';

    // if (column.endsWith('_id') || column.endsWith('_ids')) return '15vh'; // Covered by specific array column widths now
    if (column === 'id' || column === 'status' || column === 'rating' || (column.startsWith('_') && !['_assertions', '_variants', '_evidence_items'].includes(column))) return '8vh'; // For _count fields not handled as arrays
    return '12vh';
  };

  const columns = getColumns();
  const tableType = id.endsWith('ies')
    ? id.slice(0, -3) + 'y'
    : id.endsWith('s')
      ? id.slice(0, -1)
      : (id === 'evidence' ? 'evidence' : id);
  return (
    <div className="OCRP-table-container">
      {showActions && (
        <div className="OCRP-table-header">
          <h2>{customTitle || `Data Table for ${id}`}</h2>
          <div className="OCRP-table-actions">
            <button className="OCRP-table-action-btn"><i className="fas fa-filter"></i> Filter</button>
            <button className="OCRP-table-action-btn"><i className="fas fa-download"></i> Export</button>
          </div>
        </div>
      )}

      <div className="OCRP-table-wrapper" ref={tableWrapperRef}>
        {error ? (
          <div className="OCRP-table-error"><i className="fas fa-exclamation-circle"></i><p>Error loading data: {error}</p></div>
        ) : (
          <table className="OCRP-data-table">
            <thead >
              <tr> 
                {columns.map((column, index) => {
                  let displayName = column;
                  if (column === 'molecular_profile_id') displayName = 'Molecular Profile';
                  else if (column === 'disease_id') displayName = 'Disease';
                  else if (column === 'therapy_ids') displayName = 'Therapies';
                  else if (column === 'evidence_ids') displayName = 'Evidence';
                  else if (column === '_assertions') displayName = 'Assertions';
                  else if (column === '_variants') displayName = 'Variants';
                  else if (column === '_evidence_items') displayName = 'Evidence Items';
                  else {
                    displayName = column.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                  }
                  return (<th key={index} style={{ width: getColumnWidth(column),  }}>
                    
                    
                    {displayName}
                    <div className="OCRP-table-header-cell-container">
                      
                      <input className="OCRP-table-header-cell-search" type="text" placeholder="Search" />
                      
                      </div>
                  
                  
                  
                  
                  </th>);
                })}
              </tr>
              
            </thead>
            <tbody >
              {loading && (!tableData || !tableData.data || tableData.data.length === 0) ? (
                <tr><td colSpan={columns.length || 1} className="OCRP-table-loading"><div className="OCRP-table-loading-spinner"></div><p>Loading data...</p></td></tr>
              ) : tableData && tableData.data && tableData.data.length > 0 ? (
                tableData.data.map((item, rowIndex) => (
                  <tr key={item.id || rowIndex}>
                    {columns.map((column, colIndex) => {
                      const cellValue = formatCellValue(column, item[column], item, tableType, onNavigate);
                      const cellContent = typeof cellValue === 'object' ? cellValue : (String(cellValue).length > 100 ? String(cellValue).substring(0, 100) + '...' : String(cellValue));
                      const cellClass = (column === 'summary' || column === 'description') ? 'OCRP-cell-expandable' : (column === 'rating' ? 'OCRP-cell-rating' : '');
                      const titleAttr = (typeof cellValue === 'object' || cellValue === null || cellValue === undefined) ? '' : String(cellValue);
                      return (<td key={colIndex} className={cellClass} title={titleAttr}>{cellContent}</td>);
                    })}
                  </tr>
                ))
              ) : !loading && (
                <tr><td colSpan={columns.length || 1} className="OCRP-table-empty-state"><i className="fas fa-table"></i><p>No data available for {id}</p></td></tr>
              )}
              {loading && tableData && tableData.data && tableData.data.length > 0 && (
                <tr className="OCRP-loading-more-row"><td colSpan={columns.length || 1}><div className="OCRP-loading-more"><div className="OCRP-table-loading-spinner"></div><span>Loading more...</span></div></td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="OCRP-table-pagination">
        <span>
          {tableData && tableData.data && tableData.data.length > 0 ? `Showing ${tableData.data.length} of ${pagination.totalRows} items` : (loading ? 'Loading...' : 'No data available')}
        </span>
        <div className="OCRP-pagination-controls">
          {pagination.page < pagination.totalPages && !loading && (
            <button className="OCRP-load-more-btn" onClick={handleLoadMore} disabled={loading}>
              Load {Math.min(uiConfig.pagination.loadMoreSize, pagination.totalRows - (tableData?.data?.length || 0))} more
            </button>
          )}
          <button disabled={!tableData || pagination.page <= 1 || loading} onClick={() => handlePageNavigation(pagination.page - 1)}><i className="fas fa-chevron-left"></i></button>
          <span>Page {pagination.page} of {pagination.totalPages || 1}</span>
          <button disabled={!tableData || pagination.page >= pagination.totalPages || loading} onClick={() => handlePageNavigation(pagination.page + 1)}><i className="fas fa-chevron-right"></i></button>
        </div>
      </div>
    </div>
  );
}

export default OncoRiskTable;